import React from "react";
import * as Sentry from "@sentry/react";
import {
  setGlobalErrorBoundaryRef,
  getGlobalErrorBoundaryRef,
  clearGlobalErrorBoundaryRef,
} from "./errorBoundaryGlobal";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
    this.errorHandler = null;
    this.rejectionHandler = null;
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    if (process.env.NODE_ENV === "development") {
      console.log("🔴 getDerivedStateFromError called with:", error);
    }
    return {
      hasError: true,
      error,
    };
  }

  componentDidMount() {
    // Set global reference so early error handlers can use it
    setGlobalErrorBoundaryRef(this);

    // Set up global error handlers to catch errors outside React's render cycle
    this.setupGlobalErrorHandlers();

    // Handle any pending errors that occurred before this component mounted
    if (typeof window !== "undefined" && window.__pendingError) {
      const { error, errorInfo } = window.__pendingError;
      delete window.__pendingError;
      this.handleError(error, errorInfo);
    }
  }

  componentWillUnmount() {
    // Clean up global error handlers
    this.removeGlobalErrorHandlers();

    // Clear global reference if this is the active one
    if (getGlobalErrorBoundaryRef() === this) {
      clearGlobalErrorBoundaryRef();
    }
  }

  setupGlobalErrorHandlers = () => {
    if (typeof window === "undefined") return;

    // Ignore ResizeObserver errors (they're usually harmless)
    const resizeObserverLoopErrRe =
      /^ResizeObserver loop completed with undelivered notifications\.$/;

    // Handle synchronous errors (event handlers, async code, etc.)
    // Note: We use a different listener than the one in index.js to avoid conflicts
    // This one runs in the bubbling phase (default), while index.js uses capture phase
    this.errorHandler = (event) => {
      // Ignore ResizeObserver errors
      if (resizeObserverLoopErrRe.test(event.message)) {
        event.stopImmediatePropagation();
        return;
      }

      // Ignore errors from scripts, stylesheets, images, etc. (only catch JS errors)
      if (event.target && event.target !== window && event.target.nodeName) {
        return;
      }

      // Only handle if this ErrorBoundary instance is the active one
      if (getGlobalErrorBoundaryRef() !== this) {
        return;
      }

      // Prevent default browser error handling
      event.preventDefault();
      event.stopPropagation();

      // Create error object with additional context
      const error = event.error || new Error(event.message || "Unknown error");
      if (event.filename) error.fileName = event.filename;
      if (event.lineno) error.lineNumber = event.lineno;
      if (event.colno) error.columnNumber = event.colno;

      // Trigger error boundary
      this.handleError(error, {
        componentStack: `Error at ${event.filename || "unknown"}:${event.lineno || "?"}:${event.colno || "?"}`,
        errorBoundary: "global-error-handler",
        type: "synchronous-error",
      });
    };

    // Handle unhandled promise rejections
    this.rejectionHandler = (event) => {
      // Only handle if this ErrorBoundary instance is the active one
      if (getGlobalErrorBoundaryRef() !== this) {
        return;
      }

      // Prevent default browser console error
      event.preventDefault();

      const error =
        event.reason instanceof Error
          ? event.reason
          : new Error(String(event.reason || "Unhandled promise rejection"));

      // Trigger error boundary
      this.handleError(error, {
        componentStack: "Unhandled Promise Rejection",
        errorBoundary: "global-error-handler",
        type: "promise-rejection",
      });
    };

    // Add listeners (bubbling phase, so they run after the capture phase handler in index.js)
    window.addEventListener("error", this.errorHandler, false);
    window.addEventListener("unhandledrejection", this.rejectionHandler, false);
  };

  removeGlobalErrorHandlers = () => {
    if (typeof window === "undefined") return;

    if (this.errorHandler) {
      window.removeEventListener("error", this.errorHandler);
      this.errorHandler = null;
    }
    if (this.rejectionHandler) {
      window.removeEventListener("unhandledrejection", this.rejectionHandler);
      this.rejectionHandler = null;
    }
  };

  componentDidCatch(error, errorInfo) {
    // Log error to console or external service (Sentry, LogRocket, etc.)
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
    if (process.env.NODE_ENV === "development") {
      console.error("Error message:", error?.message);
      console.error("Error stack:", error?.stack);
      console.error("Component stack:", errorInfo?.componentStack);
    }

    this.setState({
      errorInfo,
    });

    // Log to Sentry if available
    if (typeof Sentry !== "undefined" && typeof Sentry.captureException === "function") {
      try {
        Sentry.captureException(error, {
          contexts: {
            react: {
              componentStack: errorInfo.componentStack,
            },
          },
          extra: errorInfo,
        });
      } catch (e) {
        // Sentry not initialized, ignore
      }
    }
  }

  // Method to handle errors from global handlers
  handleError = (error, errorInfo = null) => {
    if (process.env.NODE_ENV === "development") {
      console.error("Error triggered in ErrorBoundary:", error, errorInfo);
    }

    const errorObj = error instanceof Error ? error : new Error(String(error));
    const errorInfoObj = errorInfo || {
      componentStack: error?.stack || "Global error handler",
    };

    this.setState({
      hasError: true,
      error: errorObj,
      errorInfo: errorInfoObj,
    });

    // Log to Sentry if available
    if (typeof Sentry !== "undefined" && typeof Sentry.captureException === "function") {
      try {
        Sentry.captureException(errorObj, {
          contexts: {
            react: {
              componentStack: errorInfoObj.componentStack,
            },
          },
          extra: errorInfoObj,
          tags: {
            errorSource: errorInfoObj.type || "global-error-handler",
          },
        });
      } catch (e) {
        // Sentry not initialized, ignore
      }
    }
  };

  handleReload = () => {
    // Reset error state before reload
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    window.location.reload();
  };

  handleReset = () => {
    // Reset error state without reload
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    const { hasError } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      // Custom fallback (component or function)
      if (fallback) {
        return typeof fallback === "function" ? fallback(this.state) : fallback;
      }

      // Default fallback UI
      return (
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f9fafb",
            color: "#111827",
            padding: "24px",
            textAlign: "center",
          }}
        >
          <h1 style={{ fontSize: "24px", marginBottom: "12px" }}>Something went wrong</h1>

          <p style={{ maxWidth: "420px", marginBottom: "20px" }}>
            An unexpected error occurred. Please reload the page or try again later.
          </p>

          <pre
            style={{
              marginTop: "10px",
              marginBottom: "20px",
              padding: "12px",
              backgroundColor: "#f3f4f6",
              borderRadius: "6px",
              fontSize: "13px",
              whiteSpace: "pre-wrap",
              maxWidth: "800px",
              overflow: "auto",
              textAlign: "left",
            }}
          >
            {this.state.error?.toString()?.replace("custom:", "") || "Unknown error"}
            {this.state.errorInfo?.componentStack &&
              !this.state.error?.toString()?.includes("custom:") && (
                <>
                  {"\n\nComponent Stack:\n"}
                  {this.state.errorInfo.componentStack}
                </>
              )}
            {this.state.error?.stack && !this.state.error?.toString()?.includes("custom:") && (
              <>
                {"\n\nStack Trace:\n"}
                {this.state.error.stack}
              </>
            )}
          </pre>

          <div style={{ display: "flex", gap: "12px" }}>
            <button
              onClick={this.handleReload}
              style={{
                padding: "10px 18px",
                borderRadius: "6px",
                border: "none",
                backgroundColor: "#2563eb",
                color: "#ffffff",
                fontSize: "14px",
                cursor: "pointer",
                fontWeight: "500",
              }}
            >
              Reload Page
            </button>
            {/* <button
              onClick={this.handleReset}
              style={{
                padding: "10px 18px",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                backgroundColor: "#ffffff",
                color: "#111827",
                fontSize: "14px",
                cursor: "pointer",
                fontWeight: "500",
              }}
            >
              Try Again
            </button> */}
          </div>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
